import { useCallback, useMemo, useState, type DragEvent } from 'react';
import {
  endMediaToCanvasDrag,
  isMediaToCanvasDragGridActive,
  isMediaToCanvasGridDragSourceDimmed,
  updateMediaToCanvasDragDuplicateModifier,
  type MediaToCanvasGridDragSession,
} from '@/utils/mediaToCanvasDrag.utils';
import { MediaToCanvasDragSource, useMediaToCanvasDrag } from '@/hooks/useMediaToCanvasDrag';

type UseMediaToCanvasGridDragArgs = {
  source: MediaToCanvasDragSource;
  sourceNodeId: string;
  /** Node-level gate (e.g. editable). Feature flag is applied inside `useMediaToCanvasDrag`. */
  enabled: boolean;
  fileCount: number;
  getAssetKindAtIndex?: (index: number) => string | undefined;
  getPreviewUrlAtIndex?: (index: number) => string | null;
};

/**
 * Grid-only drag UX: multi-file eligibility, move-source dimming during drag, and a11y hint.
 * Composes `useMediaToCanvasDrag` for payload/preview; keep DOM wiring in `GridItem`.
 */
export function useMediaToCanvasGridDrag(args: UseMediaToCanvasGridDragArgs) {
  const { source, sourceNodeId, enabled, fileCount, getAssetKindAtIndex, getPreviewUrlAtIndex } = args;

  const { isMediaToCanvasDragAllowed, onGridDragStart } = useMediaToCanvasDrag({
    source,
    sourceNodeId,
    enabled,
    getAssetKindAtIndex,
    getPreviewUrlAtIndex,
  });

  const isGridActive = useMemo(
    () => isMediaToCanvasDragGridActive(isMediaToCanvasDragAllowed, fileCount),
    [isMediaToCanvasDragAllowed, fileCount],
  );

  const [session, setSession] = useState<MediaToCanvasGridDragSession | null>(null);

  const handleGridDragStart = useCallback(
    (event: DragEvent, index: number) => {
      onGridDragStart(event, index);
      if (!isMediaToCanvasDragAllowed || event.defaultPrevented) return;
      if (isGridActive) {
        setSession({ index, duplicateModifier: Boolean(event.altKey) });
      }
    },
    [onGridDragStart, isGridActive, isMediaToCanvasDragAllowed],
  );

  const onDragModifier = useCallback((duplicateModifier: boolean) => {
    updateMediaToCanvasDragDuplicateModifier(duplicateModifier);
    setSession((s) => (s ? { ...s, duplicateModifier } : s));
  }, []);

  const onDragEnd = useCallback(() => {
    endMediaToCanvasDrag();
    setSession(null);
  }, []);

  const isSourceDimmed = useCallback(
    (fileGlobalIndex: number) => isMediaToCanvasGridDragSourceDimmed(fileGlobalIndex, session),
    [session],
  );

  return {
    isGridActive,
    onGridDragStart: handleGridDragStart,
    onDragModifier,
    onDragEnd,
    isSourceDimmed,
    isCanvasDragSessionActive: session !== null,
  };
}
